/**CORE LIBRARIES IMPORT */
import React, { useEffect, useState } from "react";
import QRCode from "qrcode";

/**
 * One guest's ticket. The QR holds only the ticket link
 * (https://thegreywolf.ie/t#t=<opaque token>): no name, email or payment data.
 * The token is never logged or sent to analytics.
 */
export function useQrSvg(url) {
  const [state, setState] = useState({ src: null, failed: false });
  useEffect(() => {
    let cancelled = false;
    setState({ src: null, failed: false });
    // SVG: crisp at any size, no canvas needed.
    QRCode.toString(url, { type: "svg", errorCorrectionLevel: "M", margin: 4, color: { dark: "#000000", light: "#ffffff" } })
      .then((svg) => !cancelled && setState({ src: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`, failed: false }))
      .catch(() => !cancelled && setState({ src: null, failed: true }));
    return () => {
      cancelled = true;
    };
  }, [url]);
  return state;
}

const TicketQr = ({ url, seq, count, eventTitle, when, reference, test = false, status, amountDue, shareable = true }) => {
  const { src, failed } = useQrSvg(url);
  const [copied, setCopied] = useState(false);
  const label = test ? "TEST ticket" : `Ticket ${seq} of ${count}`;

  const share = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: `The Grey Wolf · ${label}`, text: `${eventTitle} · ${label}. Show this QR code at the door.`, url });
        return;
      }
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      /* cancelled by the user */
    }
  };

  return (
    <article className={`ticket-qr${test ? " ticket-qr-test" : ""}`} aria-label={label}>
      <p className="ticket-qr-brand">THE GREY WOLF</p>
      {test && <p className="ticket-qr-test-flag">TEST · NOT VALID FOR ENTRY</p>}
      <p className="event-menu-heading">{label}</p>
      {eventTitle && <p className="ticket-qr-meta">{eventTitle}{when && <><br />{when}</>}{reference && <><br />Ref {reference}</>}</p>}
      {src ? (
        <img className="ticket-qr-image" src={src} width="240" height="240" alt={`QR code, ${label}`} />
      ) : failed ? (
        <p className="details-note" role="alert">The QR code could not be shown here. It is also in your confirmation email.</p>
      ) : (
        <p className="details-note" role="status">Preparing ticket…</p>
      )}
      {status === "used" && <p className="ticket-qr-status ticket-qr-used">Already used</p>}
      {status === "not_valid" && <p className="ticket-qr-status ticket-qr-used">Not valid</p>}
      {status === "payment_due" && (
        <p className="ticket-qr-status ticket-qr-due" role="note">
          Payment due at the door{amountDue != null && <>: {new Intl.NumberFormat("en-IE", { style: "currency", currency: "EUR" }).format(amountDue / 100)} for the whole booking</>}.
          This ticket is valid for entry once the booking has been paid.
        </p>
      )}
      <p className="details-note">Admits one person, once.</p>
      {shareable && (
        <button type="button" className="hero-btn" onClick={share}>
          {copied ? "Link copied" : "Share this ticket"}
        </button>
      )}
    </article>
  );
};

export default TicketQr;
